import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Alert,
  Link,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

interface LoginForm {
  email: string;
  password: string;
}

interface LocationState {
  from?: {
    pathname: string;
  };
  message?: string;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();
  const [error, setError] = React.useState<string | null>(null);
  
  const state = location.state as LocationState;
  const from = state?.from?.pathname || '/';
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    state?.message || null
  );
  
  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    try {
      setError(null);
      setSuccessMessage(null);
      await login(data.email, data.password);
      
      console.log('Redirecting to:', from);
      navigate(from, { replace: true });
    } catch (err: any) {
      if (err.response?.data?.detail) {
        setError(typeof err.response.data.detail === 'string' 
          ? err.response.data.detail 
          : 'Invalid email or password');
      } else {
        setError('An error occurred during login. Please try again.');
      }
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h4" sx={{ mt:5, mb: 4 }}>
          e-BIRA
        </Typography>
        <Card sx={{ width: '100%', padding: 2, border: '1px solid #ccc', borderRadius: 2}}>
          <CardContent>
            <Typography component="h2" variant="h5" sx={{ mb: 3 }}>
              Sign in
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ mt: 1 }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                autoComplete="email"
                autoFocus
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                {...register('password', {
                  required: 'Password is required',
                })}
                error={!!errors.password}
                helperText={errors.password?.message}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Sign In
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to="/forgot-password" variant="body2">
                  Forgot password?
                </Link>
              </Box>
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Link component={RouterLink} to="/ticketing" variant="body2">
                  Need Support?
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Typography component="small" sx={{ mt:5 }}>
          Copyright © {new Date().getFullYear()} <a href="https://ebira.rfsteknologi.site">ebira.rfsteknologi.site</a>.
        </Typography>
        <Typography component="small">
          All rights reserved.
        </Typography>
        <Typography component="small" sx={{ mt:5, mb: 4, fontSize: '0.85rem'}}>
          Developed by <a href="https://rstehnologi.com" target="_blank">rstehnologi.com</a>
        </Typography>
      </Box>
    </Container>
  );
}
